import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { Scale, Plus } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, StatusPill, type ColumnDef, type StatusTone } from '@vnetwork/ui/data';
import { Button } from '@vnetwork/ui/primitives';
import { useLoadBalancers, useLoadBalancer, type LoadBalancer } from '@/api/opennebula';
import { LoadBalancerCreate } from './loadbalancers/LoadBalancerCreate';
import { LoadBalancerDetail } from './loadbalancers/LoadBalancerDetail';

function tone(s?: unknown): StatusTone {
  const v = String(s ?? '').toLowerCase();
  if (v.includes('run') || v.includes('active') || v.includes('on')) return 'success';
  if (v.includes('pend') || v.includes('deploy')) return 'warning';
  if (v.includes('error') || v.includes('fail')) return 'danger';
  return 'muted';
}

// The list endpoint returns DB-only records — status is stale and NICs/listeners
// aren't populated (they live in OpenNebula). Each row pulls the live detail (same
// source as the detail page) so the list matches it. react-query dedupes the three
// cells to one request per row, keyed identically to the detail page.
function useLiveLb(serviceId: string | undefined, lb: LoadBalancer): LoadBalancer {
  const { data } = useLoadBalancer(serviceId, lb.id);
  return data ?? lb;
}

function PublicIpCell({ serviceId, lb }: { serviceId?: string; lb: LoadBalancer }) {
  const live = useLiveLb(serviceId, lb);
  const ip = live.metadata?.nics?.find((n) => /public/i.test(n.network ?? ''))?.ip || live.ip;
  return <span style={{ color: 'var(--vn-fg-muted)', fontVariantNumeric: 'tabular-nums' }}>{ip || '—'}</span>;
}

function ListenersCell({ serviceId, lb }: { serviceId?: string; lb: LoadBalancer }) {
  const live = useLiveLb(serviceId, lb);
  return <>{live.metadata?.listeners?.length ?? 0}</>;
}

function StatusCell({ serviceId, lb }: { serviceId?: string; lb: LoadBalancer }) {
  const live = useLiveLb(serviceId, lb);
  const s = live.status || live.state;
  return <StatusPill tone={tone(s)}>{s || 'Unknown'}</StatusPill>;
}

/** Load balancers sub-router: list · create · detail (with tabs). */
export function LoadBalancers() {
  return (
    <Routes>
      <Route index element={<LoadBalancerList />} />
      <Route path="create" element={<LoadBalancerCreate />} />
      <Route path=":lbId/*" element={<LoadBalancerDetail />} />
    </Routes>
  );
}

function LoadBalancerList() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const navigate = useNavigate();
  const { data: lbs = [], isLoading } = useLoadBalancers(serviceId);
  const base = `/services/compute/${serviceId}`;

  const columns: ColumnDef<LoadBalancer, unknown>[] = [
    { id: 'name', header: 'Name', cell: ({ row }) => <span style={{ fontWeight: 500 }}>{row.original.name || row.original.id}</span> },
    { id: 'ip', header: 'Public IP', cell: ({ row }) => <PublicIpCell serviceId={serviceId} lb={row.original} /> },
    { id: 'listeners', header: 'Listeners', cell: ({ row }) => <ListenersCell serviceId={serviceId} lb={row.original} /> },
    { id: 'status', header: 'Status', cell: ({ row }) => <StatusCell serviceId={serviceId} lb={row.original} /> },
  ];

  return (
    <Container size="xl">
      <PageHeader
        icon={<Scale size={22} />}
        title="Load balancers"
        description="Distribute traffic across your instances."
        actions={<Button size="sm" startIcon={<Plus size={15} />} onClick={() => navigate(`${base}/load-balancers/create`)}>New load balancer</Button>}
      />
      {/* Empty placeholder renders inside the table's own bordered frame
          (via `emptyState`), so the zone keeps the same frame whether empty or full. */}
      {isLoading ? null : (
        <DataTable
          columns={columns}
          data={lbs}
          getRowId={(l) => String(l.id)}
          onRowClick={(l) => navigate(`${base}/load-balancers/${l.id}`)}
          emptyState={
            <EmptyState
              icon={<Scale size={28} />}
              title="No load balancers"
              description="Create a load balancer to front your instances."
              actions={<Button size="sm" startIcon={<Plus size={15} />} onClick={() => navigate(`${base}/load-balancers/create`)}>New load balancer</Button>}
            />
          }
        />
      )}
    </Container>
  );
}
